import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';

import Breadcrumbs from 'components/Core/Breadcrumbs';
import Pagination from 'components/Pagination';
import AccommodationCard from 'components/AccommodationCard';
import IconSvg from 'components/Core/IconSvg';
import Loading from 'components/Core/Loading';

import { getPageOnHostByID, performQuery } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import trackAction from 'utils/tracking';

import { AppContext } from 'context/AppContext';

import AccommodationModal from './AccommodationModal';
import AccommodationUpdateQuery from './AccommodationUpdate.query';

const AccommodationIndex = (props) => {
  const {
    page: { url, title, breadcrumbs },
    accommodations: { items, pagination },
    lat,
    lon,
    name,
    noCookie,
    currentPage,
  } = props;
  const router = useRouter();
  const { updateAppState, showAccommodationModal } = useContext(AppContext);
  const [city, setCity] = useState({
    lat,
    lon,
    name,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [accommodationsList, setAccommodationsList] = useState({
    itemsList: items,
    paginationObj: pagination,
  });

  useEffect(() => {
    setAccommodationsList({
      itemsList: items,
      paginationObj: pagination,
    });
  }, [items, pagination]);

  const updateAccommodations = async (cityObj) => {
    setIsLoading(true);
    const { lat: latitude, lon: longitude } = cityObj;
    await performQuery(AccommodationUpdateQuery, {
      lat: latitude,
      lon: longitude,
      currentPage,
    })
      .then(({ accommodations }) =>
        setAccommodationsList({
          itemsList: accommodations.items,
          paginationObj: accommodations.pagination,
        }),
      )
      .then(setCity(cityObj))
      .then(() => setIsLoading(false));

    document.cookie = `sfnUserLocation=${JSON.stringify(cityObj)};path=/;`;
  };

  const toggleModal = () => {
    document.body.classList.toggle('modal-open');
    updateAppState((state) => {
      return {
        ...state,
        openModal: !state.openModal,
        showAccommodationModal: !state.showAccommodationModal,
      };
    });
  };

  const getGeolocation = async ({ latitude, longitude }) => {
    await performQuery(
      `query($lat: Float, $lon: Float) {
        city(lat: $lat, lon: $lon) {
          name
          lat
          lon
        }
      }`,
      {
        lat: latitude,
        lon: longitude,
      },
    ).then(({ city: cityObj }) => updateAccommodations(cityObj));
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && noCookie) {
      global.navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => getGeolocation({ latitude, longitude }),
        () => {},
        {
          enableHighAccuracy: false,
          maximumAge: Infinity,
          timeout: 12000,
        },
      );
    }
  }, []);

  const paginate = (data) => {
    const page = data.selected + 1;
    trackAction('accommodation-overview', `${page}`, 'page');
    const pathAs = `${url}?page=${page}`;
    router
      .replace(
        {
          pathname: '/[...slug]',
          query: { slug: url, page: `${page}`, lat, lon },
        },
        pathAs,
      )
      .then(() => {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
      });
  };

  const { itemsList, paginationObj } = accommodationsList;
  const { totalPages, prevPage, nextPage } = paginationObj;

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <div className="container mx-auto px-4 u-block-margins">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <div className="mb-4 md:mb-8">
            <h1 className="text-secondary mb-6">{title}</h1>
            <h2 className="mb-4 flex flex-wrap">
              <div>In de buurt van&nbsp;</div>
              <button
                type="button"
                className="text-primary flex font-bold items-center focus:outline-none"
                onClick={toggleModal}
              >
                {city.name && <IconSvg icon="location" className="text-primary" size={35} />}
                {city.name}
              </button>
            </h2>
          </div>

          {itemsList.map((accommodation) => (
            <div key={accommodation.url} className="border-b-2 border-basic-300 py-6 lg:py-10">
              <AccommodationCard {...accommodation} />
            </div>
          ))}

          {showAccommodationModal && <AccommodationModal updateLocation={updateAccommodations} />}

          {(prevPage || nextPage) && (
            <div className="my-4">
              <Pagination
                onPageClick={(data) => paginate(data)}
                totalPages={totalPages}
                initialPage={currentPage - 1}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

// The data returned here will be sent as `props` to the component
// TODO: Let's see if we need this here or if we can ultimately adopt this directly in the lib/api calls
// That will depend on how different these calls are going to be along the way.
AccommodationIndex.getCustomInitialProps = async ({
  id,
  hostname,
  previewData,
  currentPage,
  contentType,
  userLocation: { name, lat, lon, noCookie = false },
}) => {
  const query = requireQueryByName('AccommodationIndex');
  const token = previewData ? previewData.token : '';
  const { page, accommodations } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
    currentPage,
    lat,
    lon,
  });

  return {
    page,
    extraPageProps: {
      accommodations,
      lat,
      lon,
      name,
      noCookie,
      currentPage,
    },
  };
};

AccommodationIndex.defaultProps = {
  page: {
    children: [],
  },
  accommodations: {
    items: [],
    pagination: {},
  },
  name: '',
  noCookie: false,
  currentPage: 1,
};

AccommodationIndex.propTypes = {
  page: PropTypes.shape({
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string,
      }),
    ).isRequired,
  }),
  accommodations: PropTypes.shape({
    items: PropTypes.arrayOf(AccommodationCard),
    pagination: PropTypes.shape({
      total: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      prevPage: PropTypes.number,
      nextPage: PropTypes.number,
      totalPages: PropTypes.number.isRequired,
      currentPage: PropTypes.number.isRequired,
    }),
  }),
  lat: PropTypes.number.isRequired,
  lon: PropTypes.number.isRequired,
  name: PropTypes.string,
  noCookie: PropTypes.bool,
  currentPage: PropTypes.number,
};

export default AccommodationIndex;
